import React from "react";
import type { BraillerLoanManagementItem, BraillerSerialNumberSwapValidation } from '../brailler.types';
import { DialogBox } from "../../../../Components/Dialog/DialogBox";
import { HtmlDisplay } from "../../../../Components/Display";

export const BraillerSerialNumberSwapDialog = ({
    isOpen,
    onClose,
    loanItem,
    newSerialNumber,
    validationResult,
    onConfirm,
    isProcessing
}: {
    isOpen: boolean;
    onClose: () => void;
    loanItem: BraillerLoanManagementItem | null;
    newSerialNumber: string | null;
    validationResult: BraillerSerialNumberSwapValidation | null;
    onConfirm: () => void;
    isProcessing: boolean;
}) => {
const hasErrors = (validationResult?.errorMessages?.length ?? 0) > 0;
const hasWarnings = (validationResult?.warningMessages?.length ?? 0) > 0;

    const handleConfirm = () => {
        onConfirm();
    };

    // If there are errors, user can only close/cancel
    const saveLabel = hasErrors ? "Close" : (isProcessing ? "Processing..." : "Confirm & Save");

    return (
        <DialogBox
            handleCancel={onClose}
            handleSave={hasErrors ? onClose : handleConfirm}
            openDialog={isOpen}
            title="Serial Number Change Detected"
            saveLabel={saveLabel}
            saveDisabled={isProcessing}
            cancelLabel={hasErrors ? "" : "Cancel"}
        >
            {/* Error Messages - Cannot proceed */}
            {hasErrors && validationResult && (
                <div className="grid-x grid-margin-x">
                    <div className="cell">
                        <div className="callout alert">
                            <strong>Cannot Change Serial Number</strong>
                            <ul>
                                {validationResult.errorMessages.map((msg: string, idx: number) => (
                                    <li key={idx}>{msg}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Warning Messages */}
            {hasWarnings && validationResult && (
                <div className="grid-x grid-margin-x">
                    <div className="cell">
                        <div className="callout warning">
                            <strong>Warnings</strong>
                            <ul>
                                {validationResult.warningMessages.map((msg: string, idx: number) => (
                                    <li key={idx}>{msg}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            )}

            {/* Valid - Show what will happen */}
            {validationResult?.isValid && (
                <>
                    {/* Serial Number Change Section */}
                    <div className="grid-x grid-margin-x">
                        <div className="cell">
                            <div className="callout secondary">
                                <strong>Serial Number Change</strong>
                                <HtmlDisplay 
                                    html={`From: ${validationResult.currentSerialNumber || loanItem?.serialNumber || 'N/A'}`} 
                                    name="from-serial" 
                                />
                                <HtmlDisplay 
                                    html={`To: ${newSerialNumber}`} 
                                    name="to-serial" 
                                />
                                {validationResult.serialNumberExists ? (
                                    <HtmlDisplay 
                                        html={`Status: ${validationResult.newInventoryStatus}`} 
                                        name="new-status" 
                                    />
                                ) : (
                                    <p className="help-text">
                                        This serial number is not in the system and will be added.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid-x grid-margin-x">
                        <div className="cell">
                            <div className="callout">
                                <strong>What will happen:</strong>
                                <ul>
                                    <li>The current brailler (Serial: {validationResult.currentSerialNumber || 'N/A'}) will be returned to inventory</li>
                                    
                                    {!validationResult.serialNumberExists && (
                                        <li>A new inventory item will be created with serial number &quot;{newSerialNumber}&quot;</li>
                                    )}
                                    
                                    {validationResult.serialNumberExists && (
                                        <li>The brailler with serial number &quot;{newSerialNumber}&quot; will be assigned to this loan</li>
                                    )}
                                    
                                    {validationResult.catalogItemMismatch && (
                                        <li className="text-warning">
                                            <strong>Note:</strong> The loan will be updated to a different catalog item: {validationResult.newCatalogTitle}
                                        </li>
                                    )}
                                    
                                    <li>The same patron/student will remain on the loan</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </DialogBox>
    );
};
